import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, Lock, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";
import { Mandala } from "../../components/Mandala";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate("/admin"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); navigate("/admin"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="admin-theme min-h-screen bg-slate-900 text-slate-100 grid lg:grid-cols-2">
      <div className="relative hidden lg:flex items-center justify-center bg-gradient-to-br from-orange-700 to-amber-700 overflow-hidden">
        <Mandala className="absolute w-[600px] h-[600px] text-white/10" />
        <div className="relative text-center px-10">
          <Landmark className="w-16 h-16 mx-auto text-white mb-4" />
          <h1 className="font-display text-3xl font-extrabold text-white">Sri Mehandipur Balaji Mandir</h1>
          <p className="text-amber-100 mt-2">Temple Administration Panel</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
          <h2 className="font-display text-2xl font-bold">Staff Login</h2>
          <p className="text-sm text-slate-400 mt-1 mb-6">Sign in to manage the temple website & bookings</p>
          {error && <div className="bg-red-500/15 text-red-400 text-sm rounded-lg px-4 py-3 mb-4" data-testid="login-error">{error}</div>}
          <div className="space-y-4">
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
              <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg bg-slate-800 border border-slate-700 pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="login-email" />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
              <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-lg bg-slate-800 border border-slate-700 pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="login-password" />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 rounded-lg disabled:opacity-40 transition-colors" data-testid="admin-login-submit-button">{loading ? "Signing in…" : "Sign In"}</button>
          </div>
          <p className="text-xs text-slate-500 mt-6 text-center">Demo: puneet@simpel.ai / Balaji@2025</p>
        </form>
      </div>
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);
  if (user === null) return <div className="admin-theme min-h-screen bg-slate-900 text-slate-400 grid place-items-center">Loading…</div>;
  if (!user) return null;
  return children;
}
